import type { CompassOther, CompassPlaceInput, MinimapPlaceSpot, MinimapSpot } from "odyssey-shared";

/**
 * The map every position of the page's own map carries, as the compass band names it: one value for the player and the
 * members, so only a member the page puts on this map has a pip.
 */
export const COMPASS_MAP = 0;

const NONE: readonly CompassOther[] = [];

let spotsFor: readonly MinimapSpot[] | null = null;
let othersOf: readonly CompassOther[] = NONE;

/**
 * The compass band's view of the party members the page holds: each as a party member of the player's map, with the
 * roster place that gives the pip its colour. The result is the same array while the list is the same array, so a band
 * redrawn on every step allocates nothing for a party that has not changed.
 *
 * @param spots The party members' positions in tiles, as the minimap holds them.
 * @returns The members as the compass chooses pips from them.
 */
export function compassOthers(spots: readonly MinimapSpot[]): readonly CompassOther[] {
  if (spots.length === 0) {
    return NONE;
  }
  if (spotsFor !== spots) {
    spotsFor = spots;
    othersOf = spots.map((spot) => ({ kind: "party", handle: spot.handle, name: spot.name, place: spot.place, mapId: COMPASS_MAP, x: spot.x, y: spot.y }));
  }
  return othersOf;
}

const NO_PLACES: readonly CompassPlaceInput[] = [];

let placesFor: readonly MinimapPlaceSpot[] | null = null;
let placesOf: readonly CompassPlaceInput[] = NO_PLACES;

/**
 * The compass band's view of the known places the page holds. The minimap is only ever given discovered places, so
 * every one is marked discovered here and the choice's guard (`chooseCompassPips` skipping what is not) still stands
 * behind it. The result is the same array while the list is the same array, so a band redrawn on every step allocates
 * nothing for places that have not changed.
 *
 * @param spots The known places in tiles, as the minimap holds them.
 * @returns The places as the compass chooses pips from them.
 */
export function compassPlaces(spots: readonly MinimapPlaceSpot[]): readonly CompassPlaceInput[] {
  if (spots.length === 0) {
    return NO_PLACES;
  }
  if (placesFor !== spots) {
    placesFor = spots;
    placesOf = spots.map((spot) => ({ handle: spot.handle, name: spot.name, x: spot.x, y: spot.y, discovered: true }));
  }
  return placesOf;
}
